import React, { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  Clock3,
  FileCheck2,
  History,
  LockKeyhole,
  RefreshCw,
  ScanSearch,
  ShieldAlert,
  ShieldCheck,
  X,
  XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import MediaPane from './MediaPane';
import { createBlobRegistry } from '../lib/blobRegistry.mjs';
import {
  canSubmitRejection,
  counterpartType,
  effectivePane,
  lifecycleColor,
  lifecycleLabel,
  paneNeedsLoad,
  revisionHistory,
} from '../lib/docReviewLogic.mjs';
import { compareIdDesc, sameId } from '../lib/idUtil.mjs';
import { useAuth } from '../App';
import { useReviewInbox } from './ReviewInboxContext';
import { datumUndZeit, nurDatum } from '../lib/zeit';

// DOC-LIFECYCLE-001: Alle Medien werden autorisiert als Blob geladen. Beim
// Schließen oder Dokumentwechsel werden sämtliche Blob-URLs verworfen.

const DOC_LABELS = {
  gewerbeanmeldung: 'Gewerbeanmeldung',
  konzession: 'Konzession',
  zulassung: 'Zulassungsbescheinigung Teil I (Fahrzeugschein)',
  versicherung: 'Versicherung',
  fuehrerschein: 'Führerschein',
  fuehrerschein_front: 'Führerschein – Vorderseite',
  fuehrerschein_back: 'Führerschein – Rückseite',
  p_schein: 'P-Schein',
  p_schein_front: 'P-Schein – Vorderseite',
  p_schein_back: 'P-Schein – Rückseite',
  personalausweis: 'Personalausweis / Aufenthaltstitel',
  personalausweis_front: 'Ausweis – Vorderseite',
  personalausweis_back: 'Ausweis – Rückseite',
  profilfoto: 'Profilfoto',
  bank_nachweis: 'Bank-Nachweis',
  steuernachweis: 'Nachweis Steuernummer / USt-IdNr.',   // ADMIN-FAHRZEUG-PARTNER-SPERRE-001
  sonstiges: 'Sonstiges',
};

export const docLabel = (documentType) => DOC_LABELS[documentType] || documentType;

// FAHRER-KI-BACKEND-001 (Teil C): erkannte Dokumentart in Klartext.
const RECOGNIZED_TYPE = {
  fuehrerschein: 'Führerschein', p_schein: 'P-Schein', personalausweis: 'Personalausweis',
  aufenthaltstitel: 'Aufenthaltstitel', reisepass: 'Reisepass', sonstiges: 'anderes Dokument',
};

const KIND_LABELS = {
  initial: 'Erstupload',
  renewal: 'Erneuerung',
  correction: 'Korrektur (Reupload)',
};

// Ablauf-/Ausstellungsdaten sind reine Kalendertage (JJJJ-MM-TT) — nurDatum zeigt sie ohne Phantom-Uhrzeit.
const fmtDate = (date) => nurDatum(date, { leer: '–' });
const fmtTime = (date) => datumUndZeit(date, { jahr: 'voll', leer: '–' });

const Meta = ({ label, value, strong }) => (
  <div className="documents-review-meta-item">
    <small>{label}</small>
    <strong className={strong ? 'is-strong' : ''}>{value}</strong>
  </div>
);

const StatusPill = ({ status }) => {
  const { color, bg } = lifecycleColor(status);
  return (
    <span className="documents-review-status" style={{ color, background: bg }}>
      <i /> {lifecycleLabel(status)}
    </span>
  );
};

// FAHRER-KI-ABLAUFDATEN-BEDIENUNG-001: der Prüfbereich öffnet sich DORT, wo man ist (Ablaufdaten, Fahrer, Partner,
// Fahrzeuge) und schließt sich wieder dorthin — kein Sprung nach „Dokumente". Lädt das Dokument nach ID selbst.
export function ReviewDrawerById({ documentId, onClose, onDecided }) {
  const inbox = useReviewInbox();
  const [document, setDocument] = useState(null);
  useEffect(() => {
    let alive = true;
    setDocument(null);
    api.get('/admin/documents').then((response) => {
      const found = (response.data.documents || []).find((item) => sameId(item.id, documentId));
      if (!alive) return;
      if (found) setDocument(found);
      else { toast.error('Dokument nicht gefunden (evtl. bereits entfernt)'); onClose(); }
    }).catch(() => { if (alive) { toast.error('Dokument konnte nicht geladen werden'); onClose(); } });
    return () => { alive = false; };
  }, [documentId]);   // bewusst nur die ID: onClose ist je Render neu und darf das Laden nicht wiederholen
  if (!document) return null;
  return (
    <ReviewDrawer
      key={document.id}
      doc={document}
      onClose={onClose}
      onDecided={(id) => { try { inbox.documentDecided(id); } catch (_) {} if (onDecided) onDecided(id); }}
    />
  );
}

export default function ReviewDrawer({ doc, onClose, onDecided }) {
  const { can } = useAuth();
  const registry = useRef(null);
  if (!registry.current) registry.current = createBlobRegistry();

  const [ownerDocs, setOwnerDocs] = useState([]);
  const [panes, setPanes] = useState({});
  const [previewStatus, setPreviewStatus] = useState(doc?.preview_status || null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  const [lockReason, setLockReason] = useState('');
  // FAHRER-KI-BACKEND-001 (Teil C): Erkennung „eingetragen vs. auf dem Dokument" + Übernahme per Klick.
  const [recOverride, setRecOverride] = useState(null);   // { recognition, expires_at } nach einer Aktion
  const [manualOpen, setManualOpen] = useState(false);
  const [manualDate, setManualDate] = useState('');

  const loadPane = async (key, document) => {
    if (!document) return;
    if (!document.has_private_asset) {
      setPanes((current) => ({ ...current, [key]: { error: true, noAsset: true, forId: document.id } }));
      return;
    }
    setPanes((current) => ({ ...current, [key]: { loading: true, forId: document.id } }));
    try {
      const response = await api.get(`/admin/documents/${document.id}/content`, { responseType: 'blob' });
      const url = registry.current.register(key, response.data);
      setPanes((current) => ({ ...current, [key]: { url, mime: response.data.type, forId: document.id } }));
    } catch (error) {
      setPanes((current) => ({ ...current, [key]: { error: true, forId: document.id } }));
    }
  };

  useEffect(() => {
    if (!doc) return undefined;
    setPanes({});
    setPreviewStatus(doc.preview_status || null);
    api.get('/admin/documents', { params: { owner_type: doc.owner_type, owner_id: doc.owner_id } })
      .then((response) => setOwnerDocs(response.data.documents || []))
      .catch(() => setOwnerDocs([]));
    loadPane('current', doc);
    return () => registry.current.revokeAll();
  }, [doc?.id]);

  const otherType = counterpartType(doc?.document_type);
  const counterpart = otherType
    ? ownerDocs
      .filter((document) => document.document_type === otherType)
      .sort((left, right) => (
        ((Number(right.revision) || 1) - (Number(left.revision) || 1))
        || compareIdDesc(left.id, right.id)
      ))[0] || null
    : null;
  const effPane = effectivePane(ownerDocs, doc);
  const effective = effPane.sibling;
  const history = revisionHistory(ownerDocs, doc);

  useEffect(() => {
    if (counterpart && paneNeedsLoad(panes.counterpart, counterpart.id)) loadPane('counterpart', counterpart);
  }, [counterpart?.id, panes.counterpart]);

  useEffect(() => {
    if (effective && paneNeedsLoad(panes.effective, effective.id)) loadPane('effective', effective);
  }, [effective?.id, panes.effective]);

  if (!doc) return null;

  const retryPreview = async () => {
    try {
      const response = await api.post(`/admin/documents/${doc.id}/preview/retry`);
      setPreviewStatus(response.data.preview_status);
      toast.success(`Vorschau: ${response.data.preview_status === 'ready' ? 'neu erzeugt' : response.data.preview_status}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Retry fehlgeschlagen');
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/documents/${doc.id}/approve`);
      toast.success('Dokument freigegeben');
      if (onDecided) onDecided(doc.id);
      if (onClose) onClose();
    } catch (error) {
      const data = error.response?.data;
      if (data?.code === 'already_decided') {
        toast.error('Bereits entschieden — Ansicht wird aktualisiert.');
        if (onDecided) onDecided(doc.id);
        if (onClose) onClose();
      } else {
        toast.error(data?.error || 'Fehler bei der Freigabe');
      }
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!canSubmitRejection(reason)) {
      toast.error('Ablehnungsgrund ist Pflicht');
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/documents/${doc.id}/reject`, { reason: reason.trim() });
      toast.success('Dokument abgelehnt — der Eigentümer sieht den Grund');
      if (onDecided) onDecided(doc.id);
      if (onClose) onClose();
    } catch (error) {
      const data = error.response?.data;
      if (data?.code === 'reason_required') {
        toast.error('Ablehnungsgrund ist Pflicht');
      } else if (data?.code === 'already_decided') {
        toast.error('Bereits entschieden — Ansicht wird aktualisiert.');
        if (onDecided) onDecided(doc.id);
        if (onClose) onClose();
      } else {
        toast.error(data?.error || 'Fehler beim Ablehnen');
      }
    } finally {
      setBusy(false);
    }
  };

  const locked = Number(doc.compliance_locked) === 1;

  // ADMIN-FAHRZEUG-PARTNER-SPERRE-001 (Owner 06.10.2026): ein FREIGEGEBENES Dokument nachfordern —
  // die Freigabe gilt sofort nicht mehr, der Eigentümer sieht den Grund und lädt neu hoch.
  const requestRenewal = async () => {
    if (!canSubmitRejection(reason)) {
      toast.error('Grund für die Nachforderung ist Pflicht');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/documents/${doc.id}/request-renewal`, { reason: reason.trim() });
      toast.success('Dokument nachgefordert — die bisherige Freigabe gilt nicht mehr');
      if (onDecided) onDecided(doc.id);
      if (onClose) onClose();
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.error || 'Nachfordern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const lockCompliance = async () => {
    const trimmedReason = lockReason.trim();
    if (!trimmedReason) {
      toast.error('Sperrgrund ist Pflicht');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/documents/${doc.id}/compliance-lock`, { reason: trimmedReason });
      toast.success('Compliance-Sperre gesetzt — Konto/Fahrzeug sofort blockiert');
      if (onDecided) onDecided(doc.id);
      if (onClose) onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Sperre fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const unlockCompliance = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/documents/${doc.id}/compliance-lock`, { data: { reason: lockReason.trim() || undefined } });
      toast.success('Compliance-Sperre gelöst');
      if (onDecided) onDecided(doc.id);
      if (onClose) onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Entsperren fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const open = doc.lifecycle_status === 'pending_review';
  const paneCount = 1 + (otherType ? 1 : 0) + 1;

  // Erkennung: frischeste Sicht aus der Owner-Liste (nach Hintergrund-Lesen), sonst die Zeile selbst;
  // nach einer Übernahme/Handkorrektur gilt die Antwort der Route.
  const fresh = ownerDocs.find((document) => sameId(document.id, doc.id));
  const rec = recOverride ? recOverride.recognition : ((fresh && fresh.recognition) || doc.recognition || null);
  const recExpires = recOverride ? recOverride.expires_at : ((fresh && fresh.expires_at) || doc.expires_at || null);

  const recognitionCall = async (label, fn, { confirmedLabel = null } = {}) => {
    setBusy(true);
    try {
      const response = await fn();
      const data = response?.data || {};
      if (data.recognition) setRecOverride({ recognition: data.recognition, expires_at: data.expires_at || data.document?.expires_at || recExpires });
      const recheck = data.recheck;
      const shown = data.confirmed && confirmedLabel ? confirmedLabel : label;
      toast.success(recheck && recheck.changed && recheck.suspended === false ? `${shown} — Sperre gelöst` : shown);
      setManualOpen(false); setManualDate('');
      if (onDecided) onDecided(doc.id);   // Liste neu laden; der Drawer bleibt offen
    } catch (error) {
      toast.error(error.response?.data?.error || `${label} fehlgeschlagen`);
    } finally { setBusy(false); }
  };
  const applyDetected = () => recognitionCall('Datum übernommen', () => api.post(`/admin/documents/${doc.id}/expiry/apply-detected`));
  const saveManual = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(manualDate)) { toast.error('Datum als JJJJ-MM-TT eingeben'); return null; }
    return recognitionCall('Datum gesetzt', () => api.patch(`/admin/documents/${doc.id}/expiry`, { expires_at: manualDate }), { confirmedLabel: 'Datum bestätigt' });
  };
  const reread = () => recognitionCall('Neu gelesen', () => api.post(`/admin/documents/${doc.id}/recognize`));

  return (
    <div className="documents-review-shell" role="dialog" aria-modal="true" aria-label={`Dokumentenprüfung ${docLabel(doc.document_type)}`}>
      <button className="documents-review-overlay" onClick={onClose} aria-label="Prüfbereich schließen" />
      <aside className="documents-review-drawer">
        <header className="documents-review-heading">
          <div>
            <span className="documents-eyebrow"><LockKeyhole size={13} /> Geschützter Dokumenten-Prüfbereich</span>
            <div className="documents-review-title-row">
              <h1>{docLabel(doc.document_type)}</h1>
              <StatusPill status={doc.lifecycle_status} />
              <span className="documents-review-revision">Revision {doc.revision || 1}</span>
              {doc.revision_kind && <span className="documents-review-kind">{KIND_LABELS[doc.revision_kind] || doc.revision_kind}</span>}
            </div>
            <p>{doc.owner_name || '–'} · {doc.owner_email || 'Keine E-Mail'} · {doc.owner_type === 'partner' ? 'Partner' : doc.owner_type === 'vehicle' ? 'Fahrzeug' : 'Fahrer'}</p>
          </div>
          <button className="documents-review-close" onClick={onClose} aria-label="Schließen"><X size={17} /></button>
        </header>

        <div className="documents-review-body">
          <section className="documents-review-principle">
            <span><ShieldCheck size={17} /></span>
            <div>
              <strong>Autorisierte Vorschau · revisionsgebundene Entscheidung</strong>
              <p>Private Inhalte werden als Blob geladen und beim Schließen vollständig verworfen.</p>
            </div>
            <i>Kein Token-Link</i>
          </section>

          <section className="documents-review-stats">
            <div><span><FileCheck2 size={14} /></span><small>Revision</small><strong>r{doc.revision || 1}</strong><p>{KIND_LABELS[doc.revision_kind] || 'Dokumentenversion'}</p></div>
            <div><span><ShieldCheck size={14} /></span><small>Prüfflächen</small><strong>{paneCount}</strong><p>Neue, Gegenseite, wirksam</p></div>
            <div><span><Clock3 size={14} /></span><small>Vorschau</small><strong>{previewStatus === 'ready' || !previewStatus ? 'Bereit' : previewStatus}</strong><p>Original bleibt verfügbar</p></div>
            <div><span><ShieldAlert size={14} /></span><small>Compliance</small><strong>{locked ? 'Gesperrt' : 'Frei'}</strong><p>Slot-weiter Status</p></div>
          </section>

          <section className="documents-review-comparison">
            <header>
              <div><span className="documents-overline">Revisionsvergleich</span><h2>Dokumentflächen</h2></div>
              <span><LockKeyhole size={12} /> Zoomen, drehen und im Vollbild prüfen</span>
            </header>
            <div className="documents-review-panes">
              <MediaPane
                label={`Neue Revision (r${doc.revision || 1})`}
                sub={docLabel(doc.document_type)}
                url={panes.current?.url}
                mime={panes.current?.mime}
                loading={panes.current?.loading}
                missing={!!panes.current?.error}
                missingText="Datei konnte nicht geladen werden"
                height={280}
              />
              {counterpart && (
                <MediaPane
                  label={`Gegenseite (r${counterpart.revision || 1})`}
                  sub={docLabel(counterpart.document_type)}
                  url={panes.counterpart?.url}
                  mime={panes.counterpart?.mime}
                  loading={panes.counterpart?.loading}
                  missing={!!panes.counterpart?.error}
                  height={280}
                />
              )}
              {otherType && !counterpart && (
                <MediaPane label="Gegenseite" sub={docLabel(otherType)} missing missingText="Noch nicht hochgeladen" height={280} />
              )}
              {effPane.kind === 'self' ? (
                <MediaPane
                  label={`Aktuell freigegeben (r${doc.revision || 1})`}
                  sub="Diese Ansicht IST die aktuell wirksame Version"
                  url={panes.current?.url}
                  mime={panes.current?.mime}
                  loading={panes.current?.loading}
                  missing={!!panes.current?.error}
                  missingText="Datei konnte nicht geladen werden"
                  height={280}
                />
              ) : effPane.kind === 'sibling' ? (
                <MediaPane
                  label={`Aktuell freigegeben (r${effective.revision || 1})`}
                  sub="Diese Version ist heute wirksam"
                  url={panes.effective?.url}
                  mime={panes.effective?.mime}
                  loading={panes.effective?.loading}
                  missing={!!panes.effective?.error}
                  height={280}
                />
              ) : (
                <MediaPane label="Aktuell freigegeben" sub="Es gibt noch keine wirksame Version" missing missingText="Erstupload — kein Vergleichsstand" height={280} />
              )}
            </div>
          </section>

          <section className="documents-review-section">
            <header><span><FileCheck2 size={14} /></span><div><span className="documents-overline">Datei und Gültigkeit</span><h2>Metadaten</h2></div></header>
            <div className="documents-review-meta-grid">
              <Meta label="Ablaufdatum" value={fmtDate(recExpires)} strong />
              <Meta label="Ausstellungsdatum" value={fmtDate(doc.issued_at || doc.issue_date)} />
              <Meta label="Dokument" value={doc.display_name || docLabel(doc.document_type)} />
              <Meta label="Geprüfter MIME" value={doc.mime_verified || (doc.has_private_asset ? '–' : 'kein Inhalt')} />
              <Meta label="Hochgeladen" value={fmtTime(doc.created_at)} />
              <Meta label="Dateigröße" value={doc.file_size ? `${Math.round(doc.file_size / 1024)} kB` : '–'} />
            </div>
          </section>

          {doc.owner_type === 'driver' && rec && rec.status !== 'not_readable' && (
            <section className={`documents-review-section documents-review-recognition is-${rec.status}`}>
              <header><span><ScanSearch size={14} /></span><div><span className="documents-overline">Dokument-Erkennung · nur Hinweis</span><h2>Auf dem Dokument gelesen</h2></div></header>
              <div className="documents-review-meta-grid">
                <Meta label="Eingetragen" value={fmtDate(recExpires)} strong />
                <Meta label={rec.detected_source === 'other_side' ? `Auf der ${rec.pair.other_side === 'front' ? 'Vorderseite' : 'Rückseite'}` : 'Auf dem Dokument'} value={rec.detected ? fmtDate(rec.detected) : (rec.status === 'unread' ? 'noch nicht gelesen' : rec.status === 'conflict' ? 'zwei Daten' : rec.status === 'confirmed' ? '–' : 'nicht lesbar')} strong />
                <Meta label="Erkannt als" value={rec.fields?.detected_type ? `${RECOGNIZED_TYPE[rec.fields.detected_type] || rec.fields.detected_type}${rec.fields.detected_side ? ` · ${rec.fields.detected_side === 'back' ? 'Rückseite' : 'Vorderseite'}` : ''}` : '–'} />
                <Meta label="Gelesen" value={rec.read_at ? fmtTime(rec.read_at) : '–'} />
              </div>
              <p className={`documents-review-recognition-status is-${rec.status}`}>
                {rec.status === 'confirmed' && `Ablaufdatum von Hand ${rec.manual ? `bestätigt am ${fmtTime(rec.manual.at)}` : 'bestätigt'} — erledigt.`}
                {rec.status === 'mismatch' && 'Das eingetragene Ablaufdatum weicht vom Dokument ab.'}
                {rec.status === 'match' && 'Eingetragenes Datum und Dokument stimmen überein.'}
                {rec.status === 'detected_only' && 'Auf dem Dokument steht ein Ablaufdatum, eingetragen ist keines.'}
                {rec.status === 'unreadable' && (rec.pair && rec.pair.other_document_id && !rec.pair.other_read ? `Auf dieser Seite steht kein Ablaufdatum — die ${rec.pair.other_side === 'front' ? 'Vorderseite' : 'Rückseite'} ist noch nicht gelesen.` : 'Das Ablaufdatum war auf dem Bild nicht sicher lesbar — Handkorrektur oder Neu-Upload.')}
                {rec.status === 'conflict' && rec.conflict && `Zwei Daten erkannt: hier ${fmtDate(rec.conflict.this_side)}, auf der ${rec.pair.other_side === 'front' ? 'Vorderseite' : 'Rückseite'} ${fmtDate(rec.conflict.other_side)} — bitte von Hand entscheiden.`}
                {rec.detected_source === 'other_side' && rec.status !== 'conflict' && ` Das Datum steht auf der ${rec.pair.other_side === 'front' ? 'Vorderseite' : 'Rückseite'} und gilt für beide Seiten.`}
                {rec.status === 'unread' && 'Dieses Dokument wurde noch nicht gelesen.'}
                {rec.status === 'unsupported' && 'PDF — wird nicht gelesen.'}
                {rec.status === 'error' && `Lesen fehlgeschlagen (${rec.error_code || 'Fehler'}).`}
              </p>
              {rec.slot && rec.slot.hint && (
                <div className="documents-review-alert is-warning"><div><AlertTriangle size={14} /><span><strong>{rec.slot.hint}</strong><small>Slot und Dokument passen nicht zusammen — bitte vor der Freigabe prüfen.</small></span></div></div>
              )}
              {can && can('freigaben.manage') && (
                <div className="documents-review-recognition-actions">
                  {rec.detected && rec.status !== 'match' && rec.status !== 'confirmed' && (
                    <button className="is-dark" onClick={applyDetected} disabled={busy}><CheckCircle size={13} /> {rec.pair && rec.pair.other_document_id ? 'Für beide Seiten übernehmen' : 'Gelesenes Datum übernehmen'}</button>
                  )}
                  {manualOpen ? (
                    <>
                      <input type="date" value={manualDate} onChange={(event) => setManualDate(event.target.value)} aria-label="Ablaufdatum von Hand" />
                      <button className="is-dark" onClick={saveManual} disabled={busy || !manualDate}>Speichern</button>
                      <button className="is-neutral" onClick={() => { setManualOpen(false); setManualDate(''); }}>Abbrechen</button>
                    </>
                  ) : (
                    <button className="is-neutral" onClick={() => setManualOpen(true)} disabled={busy}>Datum von Hand</button>
                  )}
                  <button className="is-neutral" onClick={reread} disabled={busy}><RefreshCw size={12} /> {rec.status === 'unread' ? 'Jetzt lesen' : 'Neu lesen'}</button>
                </div>
              )}
            </section>
          )}

          {doc.has_private_asset && previewStatus && previewStatus !== 'ready' && (
            <section className="documents-review-alert is-warning">
              <div><AlertTriangle size={14} /><span><strong>Vorschau {previewStatus === 'pending' ? 'wird noch erzeugt' : 'fehlgeschlagen'}</strong><small>Das Original wird direkt angezeigt.</small></span></div>
              <button onClick={retryPreview}><RefreshCw size={12} /> Vorschau neu erzeugen</button>
            </section>
          )}

          <section className="documents-review-section">
            <header><span><History size={14} /></span><div><span className="documents-overline">Revisionsgebundener Verlauf</span><h2>Review- und Ablehnungshistorie</h2></div></header>
            <div className="documents-review-history">
              {history.length === 0 && <div className="documents-review-empty">Keine weiteren Revisionen.</div>}
              {history.map((item) => (
                <div className={sameId(item.id, doc.id) ? 'is-current' : ''} key={item.id}>
                  <span className="documents-review-history-main">
                    <strong>r{item.revision || 1}</strong>
                    <StatusPill status={item.lifecycle_status} />
                    {Number(item.is_effective) === 1 && <b>Wirksam</b>}
                    {sameId(item.id, doc.id) && <small>Diese Ansicht</small>}
                    {item.rejection_reason && <em>Grund: {item.rejection_reason}</em>}
                  </span>
                  <time>{fmtTime(item.created_at)}</time>
                </div>
              ))}
            </div>
          </section>

          {can && can('freigaben.manage') && (
            <section className={`documents-review-section documents-review-compliance ${locked ? 'is-locked' : ''}`}>
              <header><span><ShieldAlert size={14} /></span><div><span className="documents-overline">Reupload-feste Schutzmaßnahme</span><h2>Compliance- und Betrugssperre</h2></div></header>
              {locked ? (
                <div className="documents-review-lock-form">
                  <div className="documents-review-lock-copy is-locked">
                    <strong>Dieser Dokumentenslot ist gesperrt</strong>
                    <span>{doc.compliance_lock_reason ? `Grund: ${doc.compliance_lock_reason}` : 'Ein Reupload oder eine neue Freigabe hebt diese Sperre nicht auf.'}</span>
                  </div>
                  <input value={lockReason} onChange={(event) => setLockReason(event.target.value)} placeholder="Grund für das Entsperren (optional, wird protokolliert)" />
                  <button className="is-dark" onClick={unlockCompliance} disabled={busy}>Sperre lösen</button>
                </div>
              ) : lockOpen ? (
                <div className="documents-review-lock-form">
                  <input autoFocus value={lockReason} onChange={(event) => setLockReason(event.target.value)} placeholder="Sperrgrund (Pflicht) — interner Compliance-Vermerk" />
                  <button className="is-danger" onClick={lockCompliance} disabled={busy || !lockReason.trim()}>Slot sperren</button>
                  <button className="is-neutral" onClick={() => { setLockOpen(false); setLockReason(''); }}>Abbrechen</button>
                </div>
              ) : (
                <div className="documents-review-lock-copy">
                  <span>Nicht gesperrt. Eine Sperre blockiert Fahrer, Partner oder Fahrzeug sofort und bleibt bei Reuploads bestehen.</span>
                  <button className="is-danger-soft" onClick={() => setLockOpen(true)} disabled={busy}>Slot sperren…</button>
                </div>
              )}
            </section>
          )}
        </div>

        {open && can && can('freigaben.manage') && doc.status === 'approved' && (
          <footer className="documents-review-decision">
            <div>
              <span><ShieldCheck size={15} /></span>
              <div><small>Bereits freigegeben</small><strong>Dokument nachfordern</strong></div>
            </div>
            {renewOpen ? (
              <div className="documents-review-reject">
                <label><span>Grund der Nachforderung · für den Eigentümer sichtbar</span><input autoFocus value={reason} onChange={(event) => setReason(event.target.value)} placeholder="z. B. Steuernummer auf dem Nachweis stimmt nicht …" /></label>
                <button className="is-reject" onClick={requestRenewal} disabled={busy || !canSubmitRejection(reason)}><RefreshCw size={13} /> Nachfordern</button>
                <button className="is-cancel" onClick={() => { setRenewOpen(false); setReason(''); }}>Abbrechen</button>
              </div>
            ) : (
              <div className="documents-review-actions">
                <span className="documents-review-hint">Die Freigabe gilt nach der Nachforderung sofort nicht mehr; der Eigentümer lädt das Dokument neu hoch.</span>
                <button className="is-reject" onClick={() => setRenewOpen(true)} disabled={busy}><RefreshCw size={14} /> Nachfordern…</button>
              </div>
            )}
          </footer>
        )}
        {open && can && can('freigaben.manage') && doc.status !== 'approved' && (
          <footer className="documents-review-decision">
            <div>
              <span><ShieldCheck size={15} /></span>
              <div><small>Manuelle Entscheidung</small><strong>Dokumentenprüfung abschließen</strong></div>
            </div>
            {rejectOpen ? (
              <div className="documents-review-reject">
                <label><span>Ablehnungsgrund · für den Eigentümer sichtbar</span><input autoFocus value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Pflichtangabe …" /></label>
                <button className="is-reject" onClick={reject} disabled={busy || !canSubmitRejection(reason)}><XCircle size={13} /> Ablehnen</button>
                <button className="is-cancel" onClick={() => { setRejectOpen(false); setReason(''); }}>Abbrechen</button>
              </div>
            ) : (
              <div className="documents-review-actions">
                <button className="is-reject" onClick={() => setRejectOpen(true)} disabled={busy}><XCircle size={14} /> Ablehnen…</button>
                <button className="is-approve" onClick={approve} disabled={busy}><CheckCircle size={14} /> Freigeben</button>
              </div>
            )}
          </footer>
        )}
      </aside>
    </div>
  );
}
